import React, { useState } from 'react';
import { submitLead } from '../services/api';
import { trackAnalyticsEvent } from '../services/analytics';

interface ContactFormModalProps {
  listingId: number;
  listingName: string;
  isOpen: boolean;
  onClose: () => void;
}

const ContactFormModal: React.FC<ContactFormModalProps> = ({ listingId, listingName, isOpen, onClose }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [ownerNotified, setOwnerNotified] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name || !email) {
      setError('Please fill in your name and email.');
      return;
    }
    setSubmitting(true);
    try {
      const lead = await submitLead({ listingId, name, email, phone, message, source: 'organic' });
      trackAnalyticsEvent('generate_lead', { placement: 'contact_modal' });
      setOwnerNotified(lead.deliveryStatus === 'owner_notified');
      setSuccess(true);
    } catch {
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setName('');
    setEmail('');
    setPhone('');
    setMessage('');
    setSuccess(false);
    setOwnerNotified(false);
    setError('');
    onClose();
  };

  return (
    <div style={overlayStyle} onClick={handleClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        {/* Close Button */}
        <button onClick={handleClose} style={closeBtnStyle} aria-label="Close">✕</button>

        {success ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
            <h2 style={{ margin: '0 0 8px', fontSize: '22px', color: '#2e7d32' }}>Thank You!</h2>
            <p style={{ color: '#666', margin: '0 0 24px', fontSize: '14px' }}>
              {ownerNotified
                ? <>The verified owner of <strong>{listingName}</strong> has been notified. Your inquiry is available in their secure dashboard.</>
                : <>Your inquiry has been securely received by TopRatedRehabs. This facility has not yet enabled direct owner notifications.</>}
            </p>
            <button onClick={handleClose} style={{
              padding: '10px 32px', background: '#1a237e', color: '#fff',
              border: 'none', borderRadius: '8px', fontSize: '14px',
              fontWeight: 700, cursor: 'pointer',
            }}>Close</button>
          </div>
        ) : (
          <>
            <h2 style={{ margin: '0 0 4px', fontSize: '20px', color: '#1a237e' }}>Contact {listingName}</h2>
            <p style={{ color: '#666', margin: '0 0 20px', fontSize: '14px' }}>
              Fill out the form and a representative will reach out to you.
            </p>

            {error && (
              <div style={{
                padding: '10px 14px', background: '#ffebee', color: '#c62828',
                borderRadius: '8px', marginBottom: '14px', fontSize: '13px',
              }}>{error}</div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '14px' }}>
                <label style={labelStyle}>Name *</label>
                <input style={inputStyle} type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" />
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={labelStyle}>Email *</label>
                <input style={inputStyle} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" />
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={labelStyle}>Phone</label>
                <input style={inputStyle} type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="(555) 123-4567" />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Message</label>
                <textarea
                  style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="How can they help you?"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                style={{
                  width: '100%', padding: '12px', background: submitting ? '#9fa8da' : '#1a237e',
                  color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px',
                  fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>

            <p style={{ textAlign: 'center', color: '#aaa', fontSize: '12px', margin: '14px 0 0' }}>
              Free &amp; confidential — no obligation
            </p>
          </>
        )}
      </div>
    </div>
  );
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 10000,
  padding: '20px',
};

const modalStyle: React.CSSProperties = {
  background: '#fff', borderRadius: '16px', padding: '32px',
  maxWidth: '480px', width: '100%', position: 'relative',
  boxShadow: '0 8px 32px rgba(0,0,0,0.2)', maxHeight: '90vh',
  overflowY: 'auto',
};

const closeBtnStyle: React.CSSProperties = {
  position: 'absolute', top: '16px', right: '16px',
  background: '#f5f5f5', border: 'none', borderRadius: '50%',
  width: '32px', height: '32px', fontSize: '16px', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666',
};

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: '6px', fontSize: '13px',
  fontWeight: 600, color: '#333',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0',
  borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
  outline: 'none',
};

export default ContactFormModal;
